import type { AnswersMap } from '@/features/jobs/questions/questions'
import {
  type ChampionProviderSelection,
  type DraftJobStore,
} from '@/features/jobs/drafting/DraftJobStore'
import { filter, firstValueFrom, race, Subject } from 'rxjs'
import { bufferMap } from '@/utils/rx-util'
import { action, computed, observable, runInAction } from 'mobx'
import type { DraftJob } from '@/features/jobs/drafting/DraftJob'
import { toStream } from '@/utils/mobx-util'
import type { DraftingError } from '@/features/jobs/drafting/DraftJobViewModel'
import { map } from 'rxjs/operators'

/**
 * Types of changes for a draft.
 */
export type DraftChange =
  | UpdateAnswers
  | SetRequestedProvider
  | UpgradeLegendVersion

/**
 * Answers were changed.
 */
export interface UpdateAnswers {
  type: 'UpdateAnswers'
  answers: AnswersMap
}

/**
 * Requested Provider selection was changed.
 */
export interface SetRequestedProvider {
  type: 'SetRequestedProvider'
  selection: ChampionProviderSelection
}

/**
 * Requested Provider selection was changed.
 */
export interface UpgradeLegendVersion {
  type: 'UpgradeLegendVersion'
}

interface PersisterInput {
  seq: number
  change: DraftChange
}

/**
 * Handles persistence of changes for the draft.
 */
export class DraftPersister {
  private readonly _inputSubject = new Subject<PersisterInput>()
  private readonly _persistErrorSubject = new Subject<DraftingError>()
  private readonly _persistedSubject = new Subject<void>()

  /**
   * Emits an error when persisting fails.
   */
  readonly persistError$ = this._persistErrorSubject.asObservable()

  /**
   * Emits when the draft has been persisted.
   */
  readonly persisted$ = this._persistedSubject.asObservable()

  @observable private accessor _seq = 0
  @observable private accessor _finishedSeq = 0

  constructor(
    private readonly _job: DraftJob,
    private readonly _draftStore: DraftJobStore,
  ) {
    // Pipeline is always running.
    this._inputSubject
      .pipe(
        bufferMap(1_000, async (batch) => {
          for (const input of compactBatch(batch)) {
            await this._persist(input)
          }
          this._persistedSubject.next()
        }),
      )
      .subscribe()
  }

  /**
   * Whether any operations are pending.
   */
  @computed get isPending() {
    return this._seq > this._finishedSeq
  }

  /**
   * Enqueues a draft change.
   *
   * @param change The change.
   * @returns A promise that resolves when this change has been processed.
   */
  @action.bound enqueue(change: DraftChange) {
    const seq = ++this._seq
    const promise = this.waitUntilFlushed()
    this._inputSubject.next({ change, seq: seq })
    return promise
  }

  /**
   * Waits until the currently enqueued changes have flushed.
   */
  async waitUntilFlushed() {
    // Deference this immediately; we don't want the current seq to be
    // reactive here.
    const current = this._seq
    if (this._finishedSeq >= current) {
      // Exit immediately if already flushed.
      return true
    }

    const finished$ = toStream(() => this._finishedSeq >= current, true).pipe(
      filter(Boolean),
    )

    // Return `true` if successful, `false` if an error was emitted.
    return await firstValueFrom(
      race(finished$, this.persistError$.pipe(map(() => false))),
    )
  }

  /**
   * Persists a change.
   *
   * @param input
   * @private
   */
  private async _persist(input: PersisterInput) {
    try {
      await this._apply(input.change)
    } catch (err) {
      console.error('Error while persisting change', input.change, err)
      this._persistErrorSubject.next({
        title: 'Unable to save changes',
        error: err,
      })
    }

    runInAction(() => {
      this._finishedSeq = input.seq
    })
  }

  /**
   * Applies the change.
   *
   * @param change
   * @private
   */
  private async _apply(change: DraftChange) {
    switch (change.type) {
      case 'UpdateAnswers': {
        await this._draftStore.replaceQuestionAnswers(
          this._job.id,
          change.answers,
        )
        break
      }
      case 'UpgradeLegendVersion': {
        await this._draftStore.upgradeLegendVersion(this._job.id)
        break
      }
      case 'SetRequestedProvider': {
        await this._draftStore.requestChampionProvider(
          this._job.id,
          change.selection,
        )
        break
      }
      default: {
        console.warn('Unhandled change', change)
      }
    }
  }
}

/**
 * Compacts the batch by eliminating operations that would be superseded.
 * @param batch
 */
function compactBatch(
  batch: readonly PersisterInput[],
): readonly PersisterInput[] {
  const newBatch: PersisterInput[] = []
  const seenChangeTypes = new Set<DraftChange['type']>()

  // Iterate back so we can prioritize newer changes.
  for (let i = batch.length - 1; i >= 0; i--) {
    const input = batch[i]
    // Each change type would result in an override, so we can
    // effectively compact them by deduping them, taking the latest
    // of each type.
    if (seenChangeTypes.has(input.change.type)) {
      continue
    }

    seenChangeTypes.add(input.change.type)
    newBatch.push(input)
  }

  newBatch.reverse()
  return newBatch
}
